export function toggleFavouriteRecipe(prevFavourites, recipeName) {
  return prevFavourites.includes(recipeName)
    ? prevFavourites.filter((name) => name !== recipeName)
    : [recipeName, ...prevFavourites];
}

export function getEmptyRecipeFormState() {
  return {
    name: "",
    description: "",
    time: "",
    calories: "",
    servings: "",
    ingredients: [],
    category: "",
    tag: "",
    protein: "",
    carbs: "",
    fats: "",
    warning: "",
    hasTriedToSave: false
  };
}

export function getEmptyEditRecipeFormState() {
  return {
    editingRecipeName: null,
    name: "",
    description: "",
    time: "",
    calories: "",
    servings: "",
    ingredients: [],
    category: "",
    tag: "",
    protein: "",
    carbs: "",
    fats: "",
    warning: "",
    hasTriedToSave: false
  };
}

export function getEditRecipeFormState(recipe) {
  return {
    editingRecipeName: recipe.name,
    name: recipe.name,
    description: recipe.description || "",
    time: recipe.time === "" || recipe.time === null || recipe.time === undefined
  ? ""
  : String(recipe.time),
calories: recipe.calories === "" || recipe.calories === null || recipe.calories === undefined
  ? ""
  : String(recipe.calories),
servings: recipe.servings === "" || recipe.servings === null || recipe.servings === undefined
  ? ""
  : String(recipe.servings),
    ingredients: Array.isArray(recipe.ingredients)
      ? recipe.ingredients.map(ing =>
          (typeof ing === "object" && ing !== null)
            ? { name: ing.name || "", quantity: ing.quantity || "" }
            : { name: typeof ing === "string" ? ing : "", quantity: "" }
        )
      : [],
    category: recipe.categories?.[0] || "",
    tag: recipe.tags?.[0] || "",
    protein: recipe.macros?.protein === "" || recipe.macros?.protein === null || recipe.macros?.protein === undefined
  ? ""
  : String(recipe.macros.protein),
carbs: recipe.macros?.carbs === "" || recipe.macros?.carbs === null || recipe.macros?.carbs === undefined
  ? ""
  : String(recipe.macros.carbs),
fats: recipe.macros?.fats === "" || recipe.macros?.fats === null || recipe.macros?.fats === undefined
  ? ""
  : String(recipe.macros.fats),
    warning: "",
    hasTriedToSave: false
  };
}

export function buildRecipeFromForm({
  name,
  image,
  description,
  time,
  calories,
  servings,
  category,
  tag,
  protein,
  carbs,
  fats,
  instructions,
  ingredients,
  custom = true
}) {
  return {
    name: name.trim(),
    image: image || "",
    description: description.trim(),
    time: time === "" ? "" : Number(time),
    calories: calories === "" ? "" : Number(calories),
    servings: servings === "" ? "" : Number(servings),
    categories: [category],
    tags: tag ? [tag] : [],
    custom,
    instructions: instructions?.trim() || "",
    macros: {
      protein: protein === "" ? "" : Number(protein),
      carbs: carbs === "" ? "" : Number(carbs),
      fats: fats === "" ? "" : Number(fats)
    },
    ingredients: Array.isArray(ingredients) ? ingredients : []
  };
}
// ── Supabase ↔ app shape adapters ──────────────────────────────────────────

// Converts a Supabase row (recipes or custom_recipes) to the app's recipe shape.
// custom_recipes stores time as cook_time_minutes; recipes uses total_time_minutes.
export function dbRecipeToApp(row, isCustom = false) {
  return {
    id: row.id,
    name: row.name,
    description: row.description || "",
    image: row.image_url || "",
    time: isCustom ? (row.cook_time_minutes ?? "") : (row.total_time_minutes ?? ""),
    calories: row.calories ?? "",
    servings: row.servings ?? "",
    categories: Array.isArray(row.categories) ? row.categories : [],
    tags: Array.isArray(row.tags) ? row.tags : [],
    custom: isCustom,
    instructions: Array.isArray(row.instructions)
      ? row.instructions
          .sort((a, b) => (a?.step_number || 0) - (b?.step_number || 0))
          .map(s => (typeof s === "object" && s !== null) ? `${s.step_number}. ${s.instruction}` : s)
          .join("\n")
      : (row.instructions || ""),
    macros: {
      protein: row.protein ?? "",
      carbs: row.carbs ?? "",
      fats: row.fats ?? "",
    },
    ingredients: Array.isArray(row.ingredients) ? row.ingredients : [],
  };
}

// Converts an app recipe to a Supabase custom_recipes insert/update payload.
export function appRecipeToDbCustom(recipe, userId) {
  const num = (v) => (v !== "" && v != null ? Number(v) || null : null);
  return {
    user_id: userId,
    name: recipe.name,
    description: recipe.description || null,
    image_url: recipe.image || null,
    cook_time_minutes: num(recipe.time),
    calories: num(recipe.calories),
    protein: num(recipe.macros?.protein),
    carbs: num(recipe.macros?.carbs),
    fats: num(recipe.macros?.fats),
    ingredients: recipe.ingredients?.length ? recipe.ingredients : null,
    instructions: recipe.instructions ? [recipe.instructions] : null,
    categories: recipe.categories?.length ? recipe.categories : null,
    tags: recipe.tags?.length ? recipe.tags : null,
    servings: num(recipe.servings),
  };
}

export function mergeRecipes(savedRecipes, defaultRecipes) {
  const customRecipes = savedRecipes.filter((recipe) => recipe.custom === true);

  const savedDefaultRecipesById = new Map(
    savedRecipes
      .filter((recipe) => recipe.custom !== true && recipe.id)
      .map((recipe) => [recipe.id, recipe])
  );

  const mergedDefaultRecipes = defaultRecipes.map((defaultRecipe) => {
    const savedVersion = savedDefaultRecipesById.get(defaultRecipe.id);

    if (!savedVersion) {
      return defaultRecipe;
    }

    return {
      ...savedVersion,
      ...defaultRecipe
    };
  });

  return [...mergedDefaultRecipes, ...customRecipes];
}